<template>
    <div class="p-3">
        <div
            class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2"
        >
            <div>
                <h3 class="mb-1">Pengisian Kode ICD Pasien</h3>

                <div class="text-muted">
                    Pilih registrasi pasien untuk melihat riwayat pelayanan dan
                    melakukan pengisian ICD.
                </div>
            </div>
        </div>

        <hr />

        <div class="card border-0 shadow-sm mb-3">
            <div class="card-header bg-white fw-semibold">
                <i class="fa fa-filter text-primary me-1"></i>
                Pencarian Data
            </div>

            <div class="card-body">
                <div class="row g-3">
                    <div class="col-12 col-md-6 col-xl-3">
                        <label
                            for="tanggal_dari"
                            class="form-label small fw-semibold text-muted"
                        >
                            Tanggal Dari
                        </label>

                        <input
                            id="tanggal_dari"
                            v-model="filter.tanggal_dari"
                            type="date"
                            class="form-control"
                            :disabled="isLoading"
                            @keyup.enter="applyFilter"
                        />
                    </div>

                    <div class="col-12 col-md-6 col-xl-3">
                        <label
                            for="tanggal_sampai"
                            class="form-label small fw-semibold text-muted"
                        >
                            Tanggal Sampai
                        </label>

                        <input
                            id="tanggal_sampai"
                            v-model="filter.tanggal_sampai"
                            type="date"
                            class="form-control"
                            :disabled="isLoading"
                            @keyup.enter="applyFilter"
                        />
                    </div>

                    <div class="col-12 col-md-6 col-xl-3">
                        <label
                            for="KdTuju"
                            class="form-label small fw-semibold text-muted"
                        >
                            Jenis Pelayanan
                        </label>

                        <select
                            id="KdTuju"
                            v-model="filter.KdTuju"
                            class="form-select"
                            :disabled="isLoading"
                            @change="onJenisChange"
                        >
                            <option value="RJ">Rawat Jalan dan IGD</option>

                            <option value="RI">Rawat Inap</option>
                        </select>
                    </div>

                    <div class="col-12 col-md-6 col-xl-3">
                        <label
                            for="status_icd"
                            class="form-label small fw-semibold text-muted"
                        >
                            Status ICD
                        </label>

                        <select
                            id="status_icd"
                            v-model="filter.status_icd"
                            class="form-select"
                            :disabled="isLoading"
                        >
                            <option value="">Semua Status</option>

                            <option value="sudah">Sudah Diisi</option>

                            <option value="belum">Belum Diisi</option>
                        </select>
                    </div>

                    <div
                        v-if="filter.KdTuju === 'RI'"
                        class="col-12 col-md-6 col-xl-3"
                    >
                        <label
                            for="tanggal_pulang"
                            class="form-label small fw-semibold text-muted"
                        >
                            Tanggal Pulang
                        </label>

                        <input
                            id="tanggal_pulang"
                            v-model="filter.tanggal_pulang"
                            type="date"
                            class="form-control"
                            :disabled="isLoading"
                            @keyup.enter="applyFilter"
                        />
                    </div>

                    <div
                        v-if="filter.KdTuju === 'RI'"
                        class="col-12 col-md-6 col-xl-3"
                    >
                        <label
                            for="status_perawatan"
                            class="form-label small fw-semibold text-muted"
                        >
                            Status Perawatan
                        </label>

                        <select
                            id="status_perawatan"
                            v-model="filter.status_perawatan"
                            class="form-select"
                            :disabled="isLoading"
                        >
                            <option value="">Semua Status Perawatan</option>

                            <option value="dirawat">Masih Dirawat</option>

                            <option value="pulang">Sudah Pulang</option>
                        </select>
                    </div>

                    <div
                        class="col-12"
                        :class="{
                            'col-xl-6': filter.KdTuju === 'RI',
                        }"
                    >
                        <label
                            for="search"
                            class="form-label small fw-semibold text-muted"
                        >
                            Cari Pasien
                        </label>

                        <div class="input-group">
                            <span class="input-group-text">
                                <i class="fa fa-search"></i>
                            </span>

                            <input
                                id="search"
                                v-model="filter.search"
                                type="text"
                                class="form-control"
                                placeholder="No. RM / Nama / NIK / No. Kartu BPJS / No. Registrasi"
                                :disabled="isLoading"
                                @keyup.enter="applyFilter"
                            />
                        </div>
                    </div>
                </div>

                <div
                    v-if="invalidDateRange"
                    class="alert alert-warning py-2 mt-3 mb-0"
                >
                    <i class="fa fa-exclamation-triangle me-1"></i>
                    Tanggal sampai tidak boleh lebih kecil dari tanggal mulai.
                </div>

                <div
                    class="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3"
                >
                    <div class="d-flex flex-wrap gap-1">
                        <span class="badge bg-light text-dark border">
                            {{
                                filter.KdTuju === "RI"
                                    ? "Rawat Inap"
                                    : "Rawat Jalan dan IGD"
                            }}
                        </span>

                        <span
                            v-if="filter.tanggal_dari || filter.tanggal_sampai"
                            class="badge bg-light text-dark border"
                        >
                            {{ filter.tanggal_dari || "..." }}
                            s/d
                            {{ filter.tanggal_sampai || "..." }}
                        </span>

                        <span
                            v-if="
                                filter.KdTuju === 'RI' && filter.tanggal_pulang
                            "
                            class="badge bg-light text-dark border"
                        >
                            Pulang:
                            {{ filter.tanggal_pulang }}
                        </span>

                        <span
                            v-if="
                                filter.KdTuju === 'RI' &&
                                filter.status_perawatan
                            "
                            class="badge bg-light text-dark border"
                        >
                            {{
                                filter.status_perawatan === "pulang"
                                    ? "Sudah Pulang"
                                    : "Masih Dirawat"
                            }}
                        </span>

                        <span
                            v-if="filter.status_icd"
                            class="badge bg-light text-dark border"
                        >
                            {{
                                filter.status_icd === "sudah"
                                    ? "ICD Sudah Diisi"
                                    : "ICD Belum Diisi"
                            }}
                        </span>

                        <span
                            v-if="filter.search"
                            class="badge bg-light text-dark border"
                        >
                            "{{ filter.search }}"
                        </span>
                    </div>

                    <div class="d-flex gap-2">
                        <button
                            type="button"
                            class="btn btn-outline-danger"
                            :disabled="isLoading"
                            @click="resetFilters"
                        >
                            <i class="fa fa-refresh me-1"></i>
                            Reset
                        </button>

                        <button
                            type="button"
                            class="btn btn-primary"
                            :disabled="isLoading || invalidDateRange"
                            @click="applyFilter"
                        >
                            <i
                                class="fa me-1"
                                :class="
                                    isLoading
                                        ? 'fa-spinner fa-spin'
                                        : 'fa-search'
                                "
                            ></i>

                            {{ isLoading ? "Memuat..." : "Cari" }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="card border-0 shadow-sm">
            <div
                class="card-header bg-white d-flex flex-wrap align-items-center justify-content-between gap-2"
            >
                <span class="fw-semibold">
                    <i class="fa fa-list-ul text-primary me-1"></i>
                    Data Pasien
                </span>

                <span v-if="totalData !== null" class="badge bg-primary">
                    {{ totalData }} registrasi
                </span>
            </div>

            <div class="card-body position-relative">
                <div v-if="isLoading" class="table-loading">
                    <div
                        class="spinner-border text-primary"
                        role="status"
                    ></div>

                    <div class="mt-2 fw-semibold text-muted">
                        Memuat data pasien...
                    </div>
                </div>

                <div class="table-responsive-wrapper">
                    <div id="table-icd"></div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { TabulatorFull as Tabulator } from "tabulator-tables";

const STORAGE_KEY = "icd_pasien_list_state";

const defaultFilter = () => ({
    search: "",
    tanggal_dari: "",
    tanggal_sampai: "",
    tanggal_pulang: "",
    KdTuju: "RJ",
    status_icd: "",
    status_perawatan: "",
});

export default {
    name: "ListPasienICD",

    data() {
        return {
            table: null,
            filter: defaultFilter(),
            isLoading: false,
            totalData: null,
            restoredPage: 1,
            restoredPageSize: 10,
        };
    },

    computed: {
        invalidDateRange() {
            return Boolean(
                this.filter.tanggal_dari &&
                    this.filter.tanggal_sampai &&
                    this.filter.tanggal_sampai < this.filter.tanggal_dari
            );
        },
    },

    created() {
        this.restoreState();
    },

    mounted() {
        this.$nextTick(() => {
            this.initializeTable();
        });
    },

    beforeDestroy() {
        this.saveState();

        if (this.table) {
            this.table.destroy();
            this.table = null;
        }
    },

    methods: {
        initializeTable() {
            const element = document.getElementById("table-icd");

            if (!element) {
                return;
            }

            if (this.table) {
                this.table.destroy();
                this.table = null;
            }

            this.table = new Tabulator(element, {
                layout: "fitDataStretch",
                responsiveLayout: "collapse",
                placeholder: "Tidak ada data pasien yang sesuai dengan filter.",
                pagination: true,
                paginationMode: "remote",
                paginationInitialPage: this.restoredPage || 1,
                paginationSize: this.restoredPageSize || 10,
                paginationSizeSelector: [10, 25, 50, 100],
                sortMode: "remote",
                filterMode: "remote",
                ajaxURL: "/api/pasien-icd/get-all-pasien-icd",
                ajaxConfig: "GET",
                ajaxParams: () => this.buildParams(),
                ajaxRequesting: () => {
                    this.isLoading = true;
                    return true;
                },
                ajaxResponse: (url, params, response) => {
                    this.isLoading = false;

                    const pagination = response.data_pasien || {};

                    this.totalData = Number(pagination.total || 0);

                    this.saveState();

                    return {
                        data: Array.isArray(pagination.data)
                            ? pagination.data
                            : [],
                        last_page: Number(pagination.last_page || 1),
                        current_page: Number(pagination.current_page || 1),
                        total: Number(pagination.total || 0),
                    };
                },
                ajaxError: (error) => {
                    this.isLoading = false;
                    this.totalData = 0;

                    this.showError(
                        error?.response?.data?.message ||
                            error?.message ||
                            "Data pasien gagal dimuat."
                    );
                },
                columnDefaults: {
                    headerSort: false,
                    vertAlign: "middle",
                },
                columns: [
                    {
                        title: "No.",
                        width: 65,
                        hozAlign: "center",
                        formatter: (cell) => {
                            const rowPosition = cell.getRow().getPosition(true);

                            const currentPage = this.table
                                ? this.table.getPage()
                                : 1;

                            const pageSize = this.table
                                ? this.table.getPageSize()
                                : 10;

                            return (currentPage - 1) * pageSize + rowPosition;
                        },
                    },
                    {
                        title: "No. Registrasi",
                        field: "Regno",
                        width: 145,
                        minWidth: 145,
                        formatter: (cell) => {
                            return `<span class="fw-semibold">${this.escapeHtml(
                                cell.getValue()
                            )}</span>`;
                        },
                    },
                    {
                        title: "No. RM",
                        field: "Medrec",
                        width: 115,
                        minWidth: 115,
                    },
                    {
                        title: "Nama Pasien",
                        field: "Firstname",
                        minWidth: 190,
                    },
                    {
                        title: "Poli Asal",
                        field: "NMPoli",
                        minWidth: 160,
                        formatter: (cell) => this.displayValue(cell.getValue()),
                    },
                    {
                        title: "Ruang Rawat",
                        field: "NmBangsal",
                        minWidth: 160,
                        visible: this.filter.KdTuju === "RI",
                        formatter: (cell) => this.displayValue(cell.getValue()),
                    },
                    {
                        title: "DPJP",
                        field: "NmDoc",
                        minWidth: 180,
                        formatter: (cell) => this.displayValue(cell.getValue()),
                    },
                    {
                        title: "Waktu Registrasi",
                        field: "date",
                        width: 165,
                        minWidth: 165,
                        formatter: (cell) => this.displayValue(cell.getValue()),
                    },
                    {
                        title: "Tanggal Masuk Rawat",
                        field: "TanggalMasukRawat",
                        width: 150,
                        minWidth: 150,
                        hozAlign: "center",
                        visible: this.filter.KdTuju === "RI",
                        formatter: (cell) => this.displayValue(cell.getValue()),
                    },
                    {
                        title: "Tanggal Pulang",
                        field: "TanggalPulang",
                        width: 135,
                        minWidth: 135,
                        hozAlign: "center",
                        visible: this.filter.KdTuju === "RI",
                        formatter: (cell) => this.displayValue(cell.getValue()),
                    },
                    {
                        title: "Status Perawatan",
                        field: "StatusPerawatan",
                        width: 150,
                        minWidth: 150,
                        hozAlign: "center",
                        formatter: (cell) => {
                            const status = String(cell.getValue() || "");

                            if (status === "Masih Dirawat") {
                                return `
                                    <span class="badge bg-warning text-dark">
                                        Masih Dirawat
                                    </span>
                                `;
                            }

                            if (status === "Sudah Pulang") {
                                return `
                                    <span class="badge bg-success">
                                        Sudah Pulang
                                    </span>
                                `;
                            }

                            return `
                                <span class="badge bg-info text-dark">
                                    Rawat Jalan / IGD
                                </span>
                            `;
                        },
                    },
                    {
                        title: "Status ICD",
                        field: "diu_kd_icd",
                        width: 125,
                        minWidth: 125,
                        hozAlign: "center",
                        formatter: (cell) => {
                            const total = Number(cell.getValue() || 0);

                            if (total > 0) {
                                return `
                                    <span class="badge bg-success">
                                        <i class="fa fa-check-circle me-1"></i>
                                        Sudah
                                    </span>
                                `;
                            }

                            return `
                                <span class="badge bg-secondary">
                                    <i class="fa fa-times-circle me-1"></i>
                                    Belum
                                </span>
                            `;
                        },
                    },
                    {
                        title: "Aksi",
                        width: 110,
                        minWidth: 110,
                        hozAlign: "center",
                        formatter: () => {
                            return `
                                <button
                                    type="button"
                                    class="btn btn-sm btn-primary btn-view-icd"
                                    title="Lihat riwayat dan isi ICD"
                                >
                                    <i class="fa fa-eye me-1"></i>
                                    View
                                </button>
                            `;
                        },
                        cellClick: (event, cell) => {
                            const button =
                                event.target.closest(".btn-view-icd");

                            if (!button) {
                                return;
                            }

                            this.openDetailPage(cell.getRow().getData());
                        },
                    },
                ],
            });

            this.syncRawatInapColumns();

            this.table.on("pageLoaded", () => {
                this.saveState();
            });

            this.table.on("dataLoaded", () => {
                this.isLoading = false;
                this.saveState();
            });

            this.table.on("dataLoadError", () => {
                this.isLoading = false;
            });
        },

        buildParams() {
            const params = {};

            Object.entries(this.filter).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== "") {
                    params[key] = value;
                }
            });

            return params;
        },

        applyFilter() {
            if (this.invalidDateRange) {
                this.showError(
                    "Tanggal sampai tidak boleh lebih kecil dari tanggal mulai."
                );

                return;
            }

            this.restoredPage = 1;

            this.saveState(1);

            if (this.table) {
                this.table.setPage(1);
            }

            this.reloadTable();
        },

        onJenisChange() {
            if (this.filter.KdTuju !== "RI") {
                this.filter.status_perawatan = "";

                this.filter.tanggal_pulang = "";
            }

            this.totalData = null;
            this.restoredPage = 1;

            this.saveState(1);

            this.syncRawatInapColumns();

            if (this.table) {
                this.table.setPage(1);
            }

            this.reloadTable();
        },

        syncRawatInapColumns() {
            if (!this.table) {
                return;
            }

            const ruangRawat = this.table.getColumn("NmBangsal");

            const tanggalMasuk = this.table.getColumn("TanggalMasukRawat");

            const tanggalPulang = this.table.getColumn("TanggalPulang");

            const isRawatInap = this.filter.KdTuju === "RI";

            if (ruangRawat) {
                if (isRawatInap) {
                    ruangRawat.show();
                } else {
                    ruangRawat.hide();
                }
            }

            if (tanggalMasuk) {
                if (isRawatInap) {
                    tanggalMasuk.show();
                } else {
                    tanggalMasuk.hide();
                }
            }

            if (tanggalPulang) {
                if (isRawatInap) {
                    tanggalPulang.show();
                } else {
                    tanggalPulang.hide();
                }
            }
        },

        reloadTable() {
            if (!this.table) {
                this.initializeTable();
                return;
            }

            this.saveState();

            this.table.setData(
                "/api/pasien-icd/get-all-pasien-icd",
                this.buildParams()
            );
        },

        openDetailPage(data) {
            const regno = String(data.Regno || "").trim();

            if (!regno) {
                this.showError("Nomor registrasi pasien tidak ditemukan.");

                return;
            }

            this.saveState();

            const isRawatInap =
                String(data.JenisPasien || "") === "RI" ||
                this.filter.KdTuju === "RI";

            this.$router.push({
                name: isRawatInap ? "updateICDRanap" : "updateICD",
                params: {
                    Regno: regno,
                },
            });
        },

        saveState(forcedPage = null) {
            try {
                let page = forcedPage !== null ? forcedPage : this.restoredPage;

                let pageSize = this.restoredPageSize;

                if (this.table) {
                    const tablePage = this.table.getPage();

                    const tablePageSize = this.table.getPageSize();

                    if (tablePage && Number(tablePage) > 0) {
                        page = Number(tablePage);
                    }

                    if (tablePageSize && Number(tablePageSize) > 0) {
                        pageSize = Number(tablePageSize);
                    }
                }

                const state = {
                    filter: {
                        ...this.filter,
                    },
                    page: Number(page) || 1,
                    pageSize: Number(pageSize) || 10,
                };

                sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));

                this.restoredPage = state.page;

                this.restoredPageSize = state.pageSize;
            } catch (error) {
                console.error(error);
            }
        },

        restoreState() {
            try {
                const saved = sessionStorage.getItem(STORAGE_KEY);

                if (!saved) {
                    return;
                }

                const state = JSON.parse(saved);

                if (state && state.filter && typeof state.filter === "object") {
                    this.filter = {
                        ...defaultFilter(),
                        ...state.filter,
                    };
                }

                if (state && Number(state.page) > 0) {
                    this.restoredPage = Number(state.page);
                }

                if (state && Number(state.pageSize) > 0) {
                    this.restoredPageSize = Number(state.pageSize);
                }
            } catch (error) {
                sessionStorage.removeItem(STORAGE_KEY);
            }
        },

        resetFilters() {
            this.filter = defaultFilter();

            this.totalData = null;

            this.restoredPage = 1;

            this.restoredPageSize = 10;

            sessionStorage.removeItem(STORAGE_KEY);

            this.syncRawatInapColumns();

            if (this.table) {
                this.table.setPageSize(10);

                this.table.setPage(1);
            }

            this.reloadTable();
        },

        displayValue(value) {
            if (value === null || value === undefined || value === "") {
                return '<span class="text-muted">-</span>';
            }

            return this.escapeHtml(value);
        },

        escapeHtml(value) {
            return String(value ?? "")
                .replaceAll("&", "&amp;")
                .replaceAll("<", "&lt;")
                .replaceAll(">", "&gt;")
                .replaceAll('"', "&quot;")
                .replaceAll("'", "&#039;");
        },

        showError(message) {
            this.$swal.fire({
                title: "Error",
                text: message,
                icon: "error",
                confirmButtonText: "OK",
            });
        },
    },
};
</script>

<style scoped>
.table-responsive-wrapper {
    width: 100%;
    overflow-x: auto;
}

.table-loading {
    position: absolute;
    inset: 0;
    z-index: 10;
    min-height: 240px;
    background: rgba(255, 255, 255, 0.86);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

:deep(.tabulator) {
    border: 1px solid #dee2e6;
    border-radius: 0.375rem;
    overflow: hidden;
}

:deep(.tabulator .tabulator-header) {
    background: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
}

:deep(.tabulator .tabulator-header .tabulator-col) {
    background: #f8f9fa;
}

:deep(.tabulator .tabulator-header .tabulator-col-content) {
    padding: 10px 8px;
}

:deep(.tabulator-row .tabulator-cell) {
    padding: 9px 8px;
}

:deep(.tabulator-row:hover) {
    background: #f4f8ff;
}

@media (max-width: 767.98px) {
    :deep(.tabulator) {
        min-width: 1000px;
    }
}
</style>
